import { colors } from '@/theme/foundations/colors';
import { KText } from '@/ui-library/components';
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Flex,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { Check, InfoCircle } from '@kula-ai/icons-core';

interface AccordionWrapperProps {
  title: string;
  count: number;
  options: any[];
  onItemClick: (item: any) => void;
  isLoading?: boolean;
  isEmptyText?: string;
  labelTooltipText?: string;
  previousSelectedFilter: any[];
}

const AccordionWrapper = ({
  labelTooltipText = undefined,
  title,
  count,
  options,
  onItemClick,
  isLoading,
  isEmptyText = 'No matching filters found.',
  previousSelectedFilter,
}: AccordionWrapperProps) => {
  const selectedFilter = previousSelectedFilter.map(
    (filter) => filter.filter_by.label
  );

  const handleItemClick = (option: any) => {
    const isPreviouslySelected = previousSelectedFilter.some(
      (filter) => filter.filter_by.label === option.label
    );

    if (isPreviouslySelected) {
      return;
    }
    onItemClick(option);
  };

  return (
    <AccordionItem>
      <AccordionButton
        _hover={{ bg: 'var(--kula-dark-secondary-50, #F1F4F9)' }}
        borderBottom="1px solid var(--kula-dark-secondary-100, #E4E5FA)"
        bg="hover-light"
        py={0}
        _focus={{
          border: 'transparent',
        }}
      >
        <Flex gap="10px">
          <Box p="12px 7px">
            <AccordionIcon />
          </Box>
          <Box borderLeft="1px solid var(--kula-dark-secondary-100, #E4E5FA)" />
          <KText
            variant="heading_2"
            p="14px 7px"
            tooltipText={labelTooltipText}
            size="sm-medium"
          >
            {title}
          </KText>
          <Box
            bg="white"
            border="1px solid var(--kula-dark-secondary-200, #DBDBDB)"
            borderRadius="6px"
            p="2px 8px"
            my="12px"
          >
            <KText>{count}</KText>
          </Box>
        </Flex>
      </AccordionButton>
      <AccordionPanel py={0} px={0}>
        {isLoading ? (
          <Box textAlign="center" mt="12px">
            <KText>Loading...</KText>
          </Box>
        ) : options.length > 0 ? (
          options.map((option, index) => {
            const isSelected = selectedFilter.includes(option.label);

            return (
              <Box
                key={option.id || option.value}
                cursor={!option?.disabled && 'pointer'}
                borderBottom={
                  index < options.length - 1 ? '1px solid #EBF2F5' : 'none'
                }
                onClick={() => !option?.disabled && handleItemClick(option)}
                bg={isSelected ? colors['hover-light'] : 'transparent'}
                _hover={{
                  bg: colors['hover-light'],
                }}
              >
                <Flex
                  px="24px"
                  py="12px"
                  justifyContent="space-between"
                  alignItems="center"
                  gap="6px"
                >
                  <Flex gap="12px">
                    <Tooltip label={option?.labelToolTip}>
                      <Box>
                        <KText opacity={option?.disabled ? 0.5 : 1} gap="12px">
                          {option.label || option.name}
                        </KText>
                      </Box>
                    </Tooltip>
                    {option?.disabledFilterInfoMessage && (
                      <Tooltip label={option?.disabledFilterInfoMessage}>
                        <Box>
                          <Icon
                            opacity={
                              option?.disabledFilterInfoMessage ? 0.5 : 1
                            }
                            // @ts-expect-error - icon is a valid IconType
                            as={InfoCircle}
                          />
                        </Box>
                      </Tooltip>
                    )}
                  </Flex>
                  {isSelected && (
                    <Check
                      size="1em"
                      color="var(--kula-colors-ats-primary)"
                      className="kula-icon"
                    />
                  )}
                </Flex>
              </Box>
            );
          })
        ) : (
          <Box textAlign="center" my="12px">
            <KText>{isEmptyText}</KText>
          </Box>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
};

export default AccordionWrapper;
